<script lang="ts">
	// Formatters
	import { format } from 'svelte-i18n';
	// Design system components
	import { Button } from '@Shelly/shelly-design-system/index';
	// Stores
	import { postReq } from 'src/services/http';
	// Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { savePeripheralSettings } from 'src/helpers/addons/addon_settings_helpers';
	// Components
	import PeripheralName from '../PeripheralName.svelte';
	import PeripheralOffset from '../PeripheralOffset.svelte';
	import PeripheralThreshold from '../PeripheralThreshold.svelte';
	import AnalogInputSettings from '../AnalogInputSettings/AnalogInputSettings.svelte';
	import AnalogCalibration from '../AnalogInputSettings/AnalogCalibration.svelte';
	import MeasurementRange from '../AnalogInputSettings/MeasurementRange.svelte';
	import CustomExpresion from '../AnalogInputSettings/CustomExpresion.svelte';
	import DigitalInputSettings from '../DigitalInputSettings/DigitalInputSettings.svelte';
	import DigitalInputType from '../DigitalInputSettings/DigitalInputType.svelte';
	import DigitalInputIcons from '../DigitalInputSettings/DigitalInputIcons.svelte';
	import SwitchOutputSettings from '../SwitchOutputSettings/SwitchOutputSettings.svelte';
	import TemperatureUnit from '../TemperatureSettings/TemperatureUnit.svelte';
	// Types
	import type { Device } from 'src/types/Device';
	import type { Peripheral, PeripheralSettings, PeripheralSettingType } from 'src/types/Peripherals';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	let className = 'mb-s';
	export { className as class };

	export let device: Device,
		peripheral: Peripheral,
		settingType: PeripheralSettingType,
		settings: PeripheralSettings = null,
		digitalInputIcons: DigitalInputIcons = null,
		settingsError = false,
		hideSaveButton = false;

	let error = '';
	let waitForResponse = false;

	const componentsMap = {
		digital_input_settings: DigitalInputSettings,
		digital_input_type: DigitalInputType,
		analog_settings: AnalogInputSettings,
		power_on_action: SwitchOutputSettings,
	};

	const save = async () => {
		waitForResponse = true;
		let res: SettingRequest;
		if (settingType == 'digital_input_icon') {
			res = await digitalInputIcons.save();
		} else {
			if (settingType == 'name') {
				device.name = settings.name;
				await postReq('interface/device/save', {
					id: device.id,
					data: JSON.stringify(device),
				});
			}
			res = await savePeripheralSettings(device, peripheral, settings);
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
		return res;
	};

	$: settingsError = error !== '';
</script>

{#if settingType in componentsMap}
	<svelte:component this={componentsMap[settingType]} class={className} bind:settings />
{:else if settingType == 'name'}
	<PeripheralName
		class={className}
		icon="fak fa-solid-sensor-on"
		label={peripheral.sensor.label}
		bind:settings
	/>
{:else if settingType == 'offset'}
	<PeripheralOffset
		class={className}
		type={peripheral.peripheralType}
		icon={peripheral.icon}
		bind:settings
		bind:error
	/>
{:else if settingType == 'threshold'}
	<PeripheralThreshold
		{device}
		class={className}
		type={peripheral.peripheralType}
		icon={peripheral.icon}
		bind:settings
		bind:error
	/>
{:else if settingType == 'temperature_units'}
	<TemperatureUnit {device} />
{:else if settingType == 'digital_input_icon'}
	<DigitalInputIcons class={className} {device} {peripheral} bind:this={digitalInputIcons} />
{:else if settingType == 'measurement_range'}
	<MeasurementRange {device} class={className} icon={peripheral.icon} bind:settings />
{:else if settingType == 'analog_calibration'}
	<AnalogCalibration class={className} {device} bind:settings />
{:else if settingType == 'voltmeter_custom_expression'}
	<CustomExpresion {device} type="xvoltage" bind:settings />
{:else if settingType == 'analog_custom_expression'}
	<CustomExpresion {device} type="xpercent" bind:settings />
{/if}

{#if !hideSaveButton && settingType !== 'temperature_units'}
	<Button
		class="width-full"
		size="m"
		on:click={save}
		disabled={settingsError}
		loading={waitForResponse}
	>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
